import React, { useState, useEffect } from "react";
import {
  Card,
  Typography,
  Button,
  Divider,
  Row,
  Col,
  Statistic,
  Spin,
  Result,
} from "antd";
import {
  ClockCircleOutlined,
  FileTextOutlined,
  HomeOutlined,
} from "@ant-design/icons";
import { useNavigate, useParams } from "react-router-dom";
import { useAuth } from "../auth/AuthContext";
import { useCheckout } from "./CheckoutContext";
import { useReferral } from "../../utils/referral";

const { Title, Text } = Typography;

// Helper function to format the order type for display
const formatOrderType = (type: string) => {
  if (!type) return "Booking";
  // Capitalizes the first letter and adds " Booking"
  return `${type.charAt(0).toUpperCase() + type.slice(1)} Booking`;
};

const CheckoutPaymentStatus = (props: any) => {
  const { navigateWithReferral } = useReferral();
  const { bookingId } = useParams();
  const { user } = useAuth();
  const { checkPaymentStatus, loading } = useCheckout();

  const [order, setOrder] = useState<any>(null);

  const check_payment_status = async (params: any) => {
    const res = await checkPaymentStatus(params);
    console.log("check res ===> ", res);
    setOrder(res);
  };

  useEffect(() => {
    if (bookingId && user?.UUID) {
      check_payment_status({ UUID: user?.UUID, order_number: bookingId });
    }
  }, [bookingId, user]);

  const renderStatusContent = () => {
    if (loading) {
      return (
        <div style={{ textAlign: "center", padding: "50px 0" }}>
          <Spin size="large" />
          <Title level={3} style={{ marginTop: "20px" }}>
            Verifying Payment...
          </Title>
          <Text>Please wait while we confirm your payment status.</Text>
        </div>
      );
    }

    if (!order) {
      return (
        <Result status="error" title="Could not retrieve order details." />
      );
    }

    // Prepare the subtitle with the order type
    const subtitle = `Order Number: #${order.order_id} (${formatOrderType(
      order.order_type
    )})`;

    switch (order.payment_status) {
      case "success":
        return (
          // @ts-ignore
          <Result
            status="success"
            title="Payment Successful!"
            subTitle={subtitle}
            extra={[
              <Button
                type="primary"
                key="home"
                icon={
                  <HomeOutlined
                    onPointerEnterCapture={undefined}
                    onPointerLeaveCapture={undefined}
                  />
                }
                onClick={() => navigateWithReferral(`/${order.order_type}`)}
              >
                New Booking
              </Button>,
              <Button
                key="booking"
                icon={
                  <FileTextOutlined
                    onPointerEnterCapture={undefined}
                    onPointerLeaveCapture={undefined}
                  />
                }
                onClick={() =>
                  navigateWithReferral(`/bookings/${order.order_type}`)
                }
              >
                Go To Booking
              </Button>,
            ]}
          >
            <div>
              <Text>
                Thank you for your booking. Please check your email for the
                booking confirmation.
              </Text>
              <Divider />
              <Row justify="center" gutter={16}>
                <Col>
                  <Statistic
                    title="Total Paid"
                    value={order.total_pay}
                    prefix="RM"
                    precision={2}
                  />
                </Col>
                {order.balance_need_to_pay > 0 && (
                  <Col>
                    <Statistic
                      title="Balance To Pay"
                      value={order.balance_need_to_pay}
                      prefix="RM"
                      precision={2}
                    />
                  </Col>
                )}
              </Row>
            </div>
          </Result>
        );
      case "pending":
        return (
          // @ts-ignore
          <Result
            icon={
              <ClockCircleOutlined
                size={10}
                color="white"
                onPointerEnterCapture={undefined}
                onPointerLeaveCapture={undefined}
              />
            }
            title="Payment is Pending"
            subTitle={subtitle}
            extra={[
              <Button
                key="booking"
                icon={
                  <FileTextOutlined
                    onPointerEnterCapture={undefined}
                    onPointerLeaveCapture={undefined}
                  />
                }
                onClick={() =>
                  navigateWithReferral(`/bookings/${order.order_type}`)
                }
              >
                Check Booking Details
              </Button>,
            ]}
          >
            <div>
              <Text>
                Your payment is currently being processed. This may take a few
                moments. Please check your booking page for updates.
              </Text>
            </div>
          </Result>
        );
      case "failed":
        return (
          // @ts-ignore
          <Result
            status="error"
            title="Payment Failed"
            subTitle={subtitle}
            extra={[
              <Button
                type="primary"
                key="retry"
                onClick={() => window.location.reload()}
              >
                Try Again
              </Button>,
              <Button
                key="booking"
                icon={
                  <FileTextOutlined
                    onPointerEnterCapture={undefined}
                    onPointerLeaveCapture={undefined}
                  />
                }
                onClick={() =>
                  navigateWithReferral(`/bookings/${order.order_number}`)
                }
              >
                Go To Booking
              </Button>,
            ]}
          >
            <Text>
              Your transaction was declined. Please try again or contact support
              if the problem persists.
            </Text>
          </Result>
        );
      default:
        return <Result status="warning" title="Unknown Payment Status." />;
    }
  };

  return (
    <Row justify="center" align="middle" style={{ minHeight: "80vh" }}>
      <Col xs={24} sm={20} md={16} lg={12} xl={10}>
        <Card bordered={false} style={{ width: "100%" }}>
          {renderStatusContent()}
        </Card>
      </Col>
    </Row>
  );
};

export default CheckoutPaymentStatus;
